import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Form, Alert } from 'react-bootstrap';
import { chatApi, chatSessionApi, pdfApi } from '../services/api';
import PDFViewerEnhanced from './PDFViewerEnhanced';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import policyAiLogo from '../assets/policy-ai-logo.svg';
import { useCampus } from '../contexts/CampusContext';

const suggestedPromptCards = [
  {
    icon: 'fa-file-signature',
    title: 'Policy lookup',
    prompt: 'What policy covers acceptable use of video surveillance?'
  },
  {
    icon: 'fa-compass',
    title: 'Find guidance',
    prompt: 'Summarize the posting of political and commercial items policy.'
  },
  {
    icon: 'fa-list-check',
    title: 'Compare rules',
    prompt: 'Compare the requirements for signage inside buildings and exterior signs.'
  }
];

const MAX_SOURCES_DISPLAY = 5;

// Extracted so React.memo can bail out of re-rendering (and re-parsing
// Markdown for) every historical message on each streamed token. During
// streaming, `messages` state updates once per animation frame, but only the
// object for the in-progress message actually gets a new reference — every
// other message keeps the same object identity across renders. Without this
// memo boundary, the whole conversation (every ReactMarkdown instance) was
// re-rendering on every frame instead of just the one bubble that changed.
const ChatMessageBubble = React.memo(function ChatMessageBubble({ message, isPendingAssistant, showCursor, onViewPDF }) {
  const isUser = message.role === 'user';
  const isAssistant = !isUser;

  const sources = message.sources || [];
  const displayedSources = sources.slice(0, MAX_SOURCES_DISPLAY);
  const hiddenCount = sources.length - displayedSources.length;

  return (
    <div className={`message ${isUser ? 'user' : 'bot'}`}>
      {isAssistant && (
        <div className="message-avatar">
          <i className="fas fa-robot"></i>
        </div>
      )}

      <div className="message-content">
        <div className="message-text markdown-body">
          {isAssistant ? (
            isPendingAssistant ? (
              <span className="assistant-waiting-inline">
                Reviewing sources
                <span className="inline-typing-dots" aria-hidden="true">
                  <span></span><span></span><span></span>
                </span>
              </span>
            ) : (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[]}
                components={{
                  // Custom rendering for code blocks
                  code({ node, inline, className, children, ...props }) {
                    return inline ? (
                      <code className="inline-code" {...props}>
                        {children}
                      </code>
                    ) : (
                      <pre className="code-block">
                        <code className={className} {...props}>
                          {children}
                        </code>
                      </pre>
                    );
                  },
                  // Custom rendering for tables
                  table({ children }) {
                    return (
                      <div className="table-responsive">
                        <table className="table table-bordered table-sm">
                          {children}
                        </table>
                      </div>
                    );
                  },
                  // Custom rendering for headings
                  h1: ({ children }) => <h4 className="mt-3 mb-2 text-primary">{children}</h4>,
                  h2: ({ children }) => <h5 className="mt-2 mb-1 text-primary">{children}</h5>,
                  h3: ({ children }) => <h6 className="mt-2 mb-1 text-secondary">{children}</h6>,
                }}
              >
                {message.content}
              </ReactMarkdown>
            )
          ) : (
            <span className="white-space-prewrap">{message.content}</span>
          )}
          {showCursor && (
            <span className="streaming-cursor">▍</span>
          )}
          {message.isError && (
            <div className="text-danger small mt-1">An error occurred while generating this response.</div>
          )}
        </div>

        {sources.length > 0 && (
          <div className="sources-container">
            <strong>Sources:</strong>
            <div>
              {displayedSources.map((source, idx) => (
                <Button
                  key={idx}
                  variant="link"
                  className="source-link p-0 me-3"
                  onClick={() => onViewPDF(source)}
                  title="Click to view PDF — jumps to the cited passage"
                >
                  {source.display_name || source.filename || `Source ${idx + 1}`}
                </Button>
              ))}
              {hiddenCount > 0 && (
                <span className="text-muted ms-2 small">+{hiddenCount} more</span>
              )}
            </div>
          </div>
        )}

        {(message.retrieval_confidence || message.generation_confidence) && (
          <div className="confidence-container">
            <div className="confidence-scores">
              <div className="confidence-score-row">
                <span className="confidence-label">Retrieval</span>
                <div className="confidence-bar">
                  <div
                    className="confidence-fill"
                    style={{
                      width: `${message.retrieval_confidence}%`,
                      backgroundColor: message.retrieval_confidence >= 70 ? '#28a745' :
                                       message.retrieval_confidence >= 40 ? '#ffc107' : '#dc3545'
                    }}
                  ></div>
                </div>
                <span className="confidence-value">{message.retrieval_confidence}%</span>
              </div>
              <div className="confidence-score-row">
                <span className="confidence-label">Generation</span>
                <div className="confidence-bar">
                  <div
                    className="confidence-fill"
                    style={{
                      width: `${message.generation_confidence}%`,
                      backgroundColor: message.generation_confidence >= 70 ? '#28a745' :
                                       message.generation_confidence >= 40 ? '#ffc107' : '#dc3545'
                    }}
                  ></div>
                </div>
                <span className="confidence-value">{message.generation_confidence}%</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {isUser && (
        <div className="message-avatar">
          <i className="fas fa-user"></i>
        </div>
      )}
    </div>
  );
});

const ChatTab = () => {
  const { campusAffiliation, campusLabel, openCampusModal } = useCampus();
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [chatSessions, setChatSessions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [currentTitle, setCurrentTitle] = useState('New Chat');
  const [error, setError] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [showPDFViewer, setShowPDFViewer] = useState(false);
  const [currentPDF, setCurrentPDF] = useState({ url: '', filename: '', searchText: null, pageNumber: null, highlights: [] });

  const pushToast = (variant, title, message, ttl = 5000) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, variant, title, message }]);
    if (ttl > 0) {
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== id));
      }, ttl);
    }
  };

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const currentController = useRef(null);
  const currentReader = useRef(null);
  const accumulatedContentRef = useRef('');
  const sourcesRef = useRef([]);

  // Initialize responsive state: collapse the sidebar when starting on (or
  // resizing into) a mobile viewport, but let the user open it afterwards.
  const wasMobileRef = useRef(typeof window !== 'undefined' && window.innerWidth <= 768);
  useEffect(() => {
    if (wasMobileRef.current) {
      setSidebarCollapsed(true);
    }

    const handleResize = () => {
      const isMobile = window.innerWidth <= 768;
      if (isMobile && !wasMobileRef.current) {
        setSidebarCollapsed(true);
      }
      wasMobileRef.current = isMobile;
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const closeSidebarOnMobile = useCallback(() => {
    if (window.innerWidth <= 768) {
      setSidebarCollapsed(true);
    }
  }, []);

  // Load chat sessions on mount
  useEffect(() => {
    loadChatSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const showWelcomeMessage = useCallback(() => {
    const welcomeMessage = {
      role: 'assistant',
      content: `Welcome to the Policy and Procedures Assistant!

I'm here to help you find information from university policies and procedures. You can ask me questions about:

• Student policies and procedures
• Faculty guidelines
• Administrative procedures
• Campus regulations
• And much more!

How can I assist you today?`,
      timestamp: new Date().toISOString(),
      sources: []
    };
    setMessages([welcomeMessage]);
  }, []);

  const createNewChat = useCallback(async (title = 'New Chat') => {
    try {
      const data = await chatSessionApi.createSession(title);
      if (data.session) {
        setCurrentSessionId(data.session.id);
        setCurrentTitle(data.session.title);
        setMessages([]);
        showWelcomeMessage();
        await loadChatSessions();
      }
    } catch (error) {
      // console.error('Error creating new chat:', error);
      setError('Failed to create new chat');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showWelcomeMessage]);

  const loadChatSession = useCallback(async (sessionId) => {
    try {
      const data = await chatSessionApi.getSession(sessionId);
      if (data.session) {
        setCurrentSessionId(sessionId);
        setCurrentTitle(data.session.title);

        if (data.session.messages && data.session.messages.length > 0) {
          setMessages(data.session.messages);
        } else {
          setMessages([]);
          showWelcomeMessage();
        }
      }
    } catch (error) {
      // console.error('Error loading chat session:', error);
      setError('Failed to load chat session');
    }
  }, [showWelcomeMessage]);

  const loadChatSessions = useCallback(async () => {
    try {
      const data = await chatSessionApi.getSessions();
      if (data.sessions) {
        setChatSessions(data.sessions);

        // If no current session, create or load one
        if (!currentSessionId && data.sessions.length === 0) {
          await createNewChat();
        } else if (!currentSessionId && data.sessions.length > 0) {
          await loadChatSession(data.sessions[0].id);
        }
      }
    } catch (error) {
      // console.error('Error loading chat sessions:', error);
      setError('Failed to load chat sessions');
    }
  }, [currentSessionId, createNewChat, loadChatSession]);

  const deleteChatSession = async (sessionId) => {
    if (!window.confirm('Are you sure you want to delete this chat session?')) {
      return;
    }

    try {
      await chatSessionApi.deleteSession(sessionId);

      if (sessionId === currentSessionId) {
        setCurrentSessionId(null);
        await createNewChat();
      }

      await loadChatSessions();
    } catch (error) {
      // console.error('Error deleting chat session:', error);
      setError('Failed to delete chat session');
    }
  };

  const updateChatTitle = async (newTitle) => {
    if (!currentSessionId) return;

    try {
      await chatSessionApi.updateSession(currentSessionId, { title: newTitle });
      setCurrentTitle(newTitle);
      await loadChatSessions();
    } catch (error) {
      // console.error('Error updating chat title:', error);
      setError('Failed to update chat title');
    }
  };

  const saveMessageToSession = async (role, content, sources = null, retrieval_confidence = null, generation_confidence = null) => {
    if (!currentSessionId) return;

    // Trim content and validate it's not empty
    const trimmedContent = content?.trim();
    if (!trimmedContent) {
      // console.warn('Attempted to save empty message, skipping');
      return;
    }

    // Ensure sources is either null or a proper array
    const cleanSources = sources && Array.isArray(sources) ? sources : null;

    // console.log('[saveMessageToSession] Saving:', { role, contentLength: trimmedContent.length, sourcesType: typeof cleanSources, sourcesLength: cleanSources?.length });

    try {
      await chatSessionApi.addMessage(currentSessionId, role, trimmedContent, cleanSources, retrieval_confidence, generation_confidence);
    } catch (error) {
      // console.error('Error saving message:', error);
      // console.error('Error response:', error.response?.data);
    }
  };

  const getConversationHistory = () => {
    // Only include messages with actual content (skip empty streaming placeholders)
    return messages
      .filter(msg => msg.content && msg.content.trim().length > 0)
      .map(msg => ({
        role: msg.role,
        content: msg.content
      }));
  };

  const addMessage = (role, content, sources = null, isError = false) => {
    const message = {
      role: role === 'user' ? 'user' : 'assistant',
      content,
      timestamp: new Date().toISOString(),
      sources,
      isError
    };

    setMessages(prev => [...prev, message]);
    return message;
  };

  const sendMessage = async () => {
    const message = inputMessage.trim();
    if (!message || isLoading) return;

    setIsLoading(true);
    setIsStreaming(true);
    setInputMessage('');
    setError(null);

    // Add user message
    addMessage('user', message);

    try {
      // Get conversation history
      const conversationHistory = getConversationHistory();

      // Create bot message placeholder immediately so the thinking indicator
      // renders while retrieval + generation are still in flight
      const botMessage = addMessage('assistant', '', null, false);
      accumulatedContentRef.current = '';
      sourcesRef.current = [];

      // Start streaming
      currentController.current = new AbortController();
      const response = await chatApi.streamChat(message, conversationHistory, campusAffiliation);

      currentReader.current = response.body.getReader();
      const decoder = new TextDecoder();
      let leftover = '';
      let completeReceived = false;

      let rafId = null;
      const flushContentUpdate = () => {
        // Throttle to one React state update per animation frame (~60 fps).
        // accumulatedContentRef always holds the latest text, so skipped frames
        // are fine – the next frame will include everything accumulated so far.
        if (rafId) return;
        rafId = requestAnimationFrame(() => {
          rafId = null;
          setMessages(prev => {
            if (!prev.length) return prev;
            const updated = [...prev];
            const lastIndex = updated.length - 1;
            // Match by timestamp – identity check (=== botMessage) breaks after
            // the first React spread because it creates a new object each time.
            if (updated[lastIndex].timestamp === botMessage.timestamp) {
              updated[lastIndex] = { ...updated[lastIndex], content: accumulatedContentRef.current };
            }
            return updated;
          });
        });
      };

      try {
        while (true) {
          const { done, value } = await currentReader.current.read();
          if (done) break;
          leftover += decoder.decode(value, { stream: true });
          const segments = leftover.split('\n');
          leftover = segments.pop();
          for (const raw of segments) {
            if (!raw.startsWith('data: ')) continue;
            const payloadText = raw.slice(6).trim();
            if (!payloadText) continue;
            try {
              const evt = JSON.parse(payloadText);
              if (evt.event === 'ping') continue; // heartbeat
              if (evt.error) {
                // Backend reported a generation failure - surface it instead
                // of silently dropping the frame
                throw Object.assign(new Error(evt.error), { fromStream: true });
              }
              if (evt.delta) {
                accumulatedContentRef.current += evt.delta;
                // Batch UI updates lightly
                flushContentUpdate();
                continue;
              }
              if (evt.complete) {
                // Cancel any pending rAF flush before we do the final atomic update
                if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
                // Final answer - use evt.answer if provided (it's the authoritative complete text)
                if (evt.answer) {
                  accumulatedContentRef.current = evt.answer;
                }
                // If generation produced no text at all, show an error instead
                // of leaving an empty bubble that gets cleaned up silently
                const generationFailed = !accumulatedContentRef.current.trim();
                if (generationFailed) {
                  accumulatedContentRef.current = 'Sorry, I could not generate a response. Please try again.';
                }
                // Attach sources & confidence
                sourcesRef.current = generationFailed ? [] : (evt.sources || sourcesRef.current);
                // Update message with complete content, sources, and confidence in one atomic operation
                setMessages(prev => {
                  if (!prev.length) return prev;
                  const updated = [...prev];
                  const lastIndex = updated.length - 1;
                  if (updated[lastIndex].timestamp === botMessage.timestamp) {
                    updated[lastIndex] = {
                      ...updated[lastIndex],
                      content: accumulatedContentRef.current,
                      sources: sourcesRef.current,
                      isError: generationFailed,
                      retrieval_confidence: generationFailed ? null : evt.retrieval_confidence,
                      generation_confidence: generationFailed ? null : evt.generation_confidence
                    };
                  }
                  return updated;
                });
                completeReceived = true;
                if (!generationFailed) {
                  // Persist messages
                  await saveMessageToSession('user', message);
                  await saveMessageToSession('assistant', accumulatedContentRef.current, sourcesRef.current, evt.retrieval_confidence, evt.generation_confidence);
                  if (currentTitle === 'New Chat') {
                    const title = generateChatTitle(message);
                    await updateChatTitle(title);
                  }
                  await loadChatSessions();
                }
                break;
              }
              // Unknown keys ignored gracefully
            } catch (e) {
              if (e.fromStream) throw e;
              // console.warn('Stream JSON parse error:', e);
            }
          }
          if (completeReceived) break;
        }
      } catch (streamErr) {
        if (streamErr.name === 'AbortError') {
          // Partial save if user stopped
          if (accumulatedContentRef.current.trim()) {
            await saveMessageToSession('user', message);
            await saveMessageToSession('assistant', accumulatedContentRef.current, sourcesRef.current);
            if (currentTitle === 'New Chat') {
              const title = generateChatTitle(message);
              await updateChatTitle(title);
            }
            await loadChatSessions();
          }
        } else {
          throw streamErr;
        }
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        // console.error('Chat error:', error);
        // Replace the empty streaming placeholder with the error message
        setMessages(prev => {
          const last = prev[prev.length - 1];
          if (last && last.role === 'assistant' && !last.content?.trim()) {
            return prev.slice(0, -1);
          }
          return prev;
        });
        addMessage('assistant', 'Sorry, I encountered an error. Please try again.', null, true);
        const msg = (error && error.message) || 'Failed to send message';
        setError(msg);
        pushToast('danger', 'Chat Error', msg);
      }
    } finally {
      // Drop the empty placeholder if the stream ended (error/abort) before
      // any content arrived, so no blank bubble is left behind
      setMessages(prev => {
        const last = prev[prev.length - 1];
        if (last && last.role === 'assistant' && !last.isError && !last.content?.trim()) {
          return prev.slice(0, -1);
        }
        return prev;
      });
      setIsLoading(false);
      setIsStreaming(false);
      currentController.current = null;
      currentReader.current = null;
    }
  };

  const stopMessage = () => {
    if (currentController.current) currentController.current.abort();
    if (currentReader.current) currentReader.current.cancel();
  };

  const generateChatTitle = (firstMessage) => {
    if (firstMessage.length > 30) {
      return firstMessage.substring(0, 27) + '...';
    }
    return firstMessage;
  };

  const formatSessionTime = (date) => {
    const now = new Date();
    const sessionDate = new Date(date);
    const diff = now - sessionDate;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor(diff / (1000 * 60));

    if (days > 0) {
      return `${days}d ago`;
    } else if (hours > 0) {
      return `${hours}h ago`;
    } else if (minutes > 0) {
      return `${minutes}m ago`;
    } else {
      return 'Just now';
    }
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleViewPDF = useCallback((source) => {
    const displayName = source.display_name || source.filename || 'Document';
    const pdfFilename = source.metadata?.source || source.metadata?.filename || source.filename;
    if (!pdfFilename) return;
    const pdfUrl = pdfApi.getPDFUrl(pdfFilename);

    // Preferred path: the backend attaches the top reranked chunks for this
    // document as `highlights` — the viewer locates and marks them directly.
    const highlights = Array.isArray(source.highlights)
      ? source.highlights.filter(h => h && typeof h.text === 'string' && h.text.trim().length >= 15)
      : [];

    if (highlights.length > 0) {
      setCurrentPDF({
        url: pdfUrl,
        filename: displayName,
        searchText: null,
        pageNumber: highlights[0].page || source.page_number || null,
        highlights
      });
      setShowPDFViewer(true);
      return;
    }

    // Legacy fallback (messages saved before highlights existed): derive a
    // search snippet from the source content preview.
    const sourceContent = source.content || source.text || null;
    let pageNumber = source.page_number || null;
    let searchText = null;

    if (sourceContent) {
      // Look for page markers: **[Page 5]** (OCR markdown) or [Page 5] (legacy)
      if (!pageNumber) {
        const pageMatch = sourceContent.match(/\*\*\[Page (\d+)\]\*\*|\[Page (\d+)\]/);
        if (pageMatch) {
          pageNumber = parseInt(pageMatch[1] || pageMatch[2], 10);
        }
      }

      // Strip markdown page markers + bold/italic markers before using as search text
      let cleanedText = sourceContent
        .replace(/\*\*\[Page \d+\]\*\*/g, '')
        .replace(/\[Page \d+\]/g, '')
        .replace(/\*\*([^*]+)\*\*/g, '$1')  // strip **bold**
        .replace(/\*([^*]+)\*/g, '$1')       // strip *italic*
        .replace(/#+\s*/g, '')               // strip markdown headings
        .replace(/---+/g, '')                // strip horizontal rules
        .replace(/\s+/g, ' ')
        .trim();

      if (cleanedText.length > 0) {
        // Split into sentences and collect up to ~300 chars of content
        const sentences = cleanedText.split(/(?<=[.!?])\s+/);
        let extractedText = '';
        for (const sentence of sentences) {
          if (extractedText.length + sentence.length <= 300) {
            extractedText += (extractedText ? ' ' : '') + sentence;
          } else if (extractedText.length === 0) {
            extractedText = sentence.substring(0, 300);
            break;
          } else {
            break;
          }
        }

        searchText = extractedText.trim();

        // If too short, widen the window
        if (searchText.length < 30 && cleanedText.length > 0) {
          searchText = cleanedText.substring(0, 350).trim();
        }

        // Give up if still too short
        if (searchText.length < 15) {
          searchText = null;
        }
      }
    }

    setCurrentPDF({ url: pdfUrl, filename: displayName, searchText, pageNumber, highlights: [] });
    setShowPDFViewer(true);
  }, []);

    const editChatTitle = () => {
    const newTitle = prompt('Enter new chat title:', currentTitle);
    if (newTitle && newTitle.trim()) {
      setCurrentTitle(newTitle.trim());
      if (currentSessionId) {
        chatSessionApi.updateSession(currentSessionId, { title: newTitle.trim() })
          .then(() => loadChatSessions())
          .catch(err => {
            // console.error('Error updating chat title:', err);
          });
      }
    }
  };

  const clearCurrentChat = () => {
    if (window.confirm('Are you sure you want to clear all messages in this chat?')) {
      setMessages([]);
      if (currentSessionId) {
        chatSessionApi.updateSession(currentSessionId, { messages: [] })
          .then(() => {
            pushToast('success', 'Chat Cleared', 'All messages have been removed from this chat.');
            loadChatSessions();
          })
          .catch(err => {
            // console.error('Error clearing chat:', err);
            pushToast('danger', 'Error', 'Failed to clear chat messages.');
          });
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleSuggestedPrompt = (prompt) => {
    setInputMessage(prompt);
    window.requestAnimationFrame(() => inputRef.current?.focus());
  };

  const hasStartedConversation = messages.some((message) => message.role === 'user');
  const showSuggestedPrompts = !hasStartedConversation && !isLoading;

  const renderMessage = (message, index) => {
    const isAssistant = message.role !== 'user';
    const isLastMessage = index === messages.length - 1;
    const isPendingAssistant = isAssistant && isLoading && isLastMessage && !message.content?.trim();
    const showCursor = isStreaming && isLastMessage && isAssistant && !!message.content?.trim();

    return (
      <ChatMessageBubble
        key={index}
        message={message}
        isPendingAssistant={isPendingAssistant}
        showCursor={showCursor}
        onViewPDF={handleViewPDF}
      />
    );
  };

  return (
    <div className={`chat-layout ${sidebarCollapsed ? 'sidebar-is-collapsed' : ''}`}>
      {/* Toasts */}
      <div className="toast-container position-fixed top-0 end-0 p-3" style={{ zIndex: 2000 }}>
        {toasts.map(t => (
          <div key={t.id} className={`toast show text-bg-${t.variant || 'info'} mb-2`} role="alert">
            <div className="toast-header">
              <strong className="me-auto">{t.title}</strong>
              <button type="button" className="btn-close" onClick={() => setToasts(prev => prev.filter(x => x.id !== t.id))}></button>
            </div>
            <div className="toast-body small">{t.message}</div>
          </div>
        ))}
      </div>
      {/* Backdrop shown behind the sidebar on mobile; tap to dismiss */}
      {!sidebarCollapsed && (
        <div
          className="chat-sidebar-backdrop"
          onClick={() => setSidebarCollapsed(true)}
          aria-hidden="true"
        />
      )}
      {/* Chat Sidebar */}
      <div className={`chat-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="chat-sidebar-header">
          <div className="chat-sidebar-brand">
            <img src={policyAiLogo} alt="" className="chat-sidebar-logo" aria-hidden="true" />
            <div>
              <p>Policy and Procedures Assistant</p>
              <span>{chatSessions.length} saved chats</span>
            </div>
          </div>
          <Button
            variant="ut-primary"
            size="sm"
            className="chat-new-btn w-100"
            onClick={() => { createNewChat(); closeSidebarOnMobile(); }}
          >
            <i className="fas fa-plus"></i>
            <span>New Chat</span>
          </Button>
        </div>

        <div className="chat-sessions-list">
          {chatSessions.length === 0 ? (
            <div className="text-center text-muted p-3">
              <i className="fas fa-comments fa-2x mb-2"></i>
              <p>No chat sessions yet</p>
            </div>
          ) : (
            chatSessions.map((session) => (
              <div
                key={session.id}
                className={`chat-session-item ${session.id === currentSessionId ? 'active' : ''}`}
                onClick={() => { loadChatSession(session.id); closeSidebarOnMobile(); }}
              >
                <div className="chat-session-main">
                  <div className="chat-session-title">{session.title}</div>
                  <div className="chat-session-meta">
                    <i className="fas fa-clock"></i>
                    {formatSessionTime(session.updated_at)}
                  </div>
                </div>
                <div className="chat-session-actions">
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0"
                    title="Delete chat"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteChatSession(session.id);
                    }}
                  >
                    <i className="fas fa-trash text-danger"></i>
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Chat Main */}
      <div className="chat-main">
        {/* Chat Header */}
        <div className="chat-header">
          <div className="chat-header-left">
            <Button
              variant="link"
              className="chat-sidebar-toggle"
              onClick={toggleSidebar}
            >
              <i className="fas fa-bars"></i>
            </Button>
            <div className="chat-title-group">
              <h5 className="chat-title">{currentTitle}</h5>
              <div className="chat-kicker">
                <span><i className="fas fa-circle"></i>Ready</span>
                <span>{messages.length} messages</span>
              </div>
            </div>
          </div>
          <div className="chat-header-right">
            <Button
              variant="link"
              size="sm"
              onClick={openCampusModal}
              title="Change campus policy scope"
              className="campus-scope-chip"
            >
              <i className="fas fa-map-marker-alt"></i>
              <span>{campusLabel || 'Select campus'}</span>
            </Button>
            <Button
              variant="link"
              size="sm"
              onClick={editChatTitle}
              title="Edit title"
              className="chat-icon-action"
            >
              <i className="fas fa-edit"></i>
            </Button>
            <Button
              variant="link"
              size="sm"
              onClick={clearCurrentChat}
              title="Clear chat"
              className="chat-icon-action danger"
            >
              <i className="fas fa-eraser"></i>
            </Button>
          </div>
        </div>

        {/* Chat Messages */}
        <div className="chat-messages">
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {messages.map((message, index) => renderMessage(message, index))}

          <div ref={messagesEndRef} />
        </div>

        {showSuggestedPrompts && (
          <div className="chat-suggested-panel" aria-label="Suggested prompts">
            {suggestedPromptCards.map((card) => (
              <button
                key={card.title}
                type="button"
                className="suggested-prompt-card"
                onClick={() => handleSuggestedPrompt(card.prompt)}
              >
                <span className="suggested-prompt-icon">
                  <i className={`fas ${card.icon}`}></i>
                </span>
                <span className="suggested-prompt-copy">
                  <span>{card.title}</span>
                  <small>{card.prompt}</small>
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Chat Input */}
        <div className="chat-composer">
          <div className="chat-input-container">
            <Form.Control
              ref={inputRef}
              type="text"
              placeholder="Ask a question about policies and procedures..."
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isLoading}
              className="chat-input"
              aria-label="Chat message"
            />

            {isStreaming ? (
              <Button
                variant="danger"
                className="chat-stop-btn"
                onClick={stopMessage}
                title="Stop generation"
                aria-label="Stop generation"
              >
                <i className="fas fa-stop"></i>
              </Button>
            ) : (
              <Button
                variant="ut-primary"
                className="chat-send-btn"
                onClick={sendMessage}
                disabled={!inputMessage.trim() || isLoading}
                title="Send message"
                aria-label="Send message"
              >
                <i className="fas fa-paper-plane"></i>
              </Button>
            )}
          </div>
          <div className="chat-disclaimer">
            Powered by large language models. Responses may contain errors — always verify against official sources.
          </div>
        </div>
      </div>

      {/* PDF Viewer Modal */}
      <PDFViewerEnhanced
        show={showPDFViewer}
        onHide={() => setShowPDFViewer(false)}
        pdfUrl={currentPDF.url}
        filename={currentPDF.filename}
        searchText={currentPDF.searchText}
        pageNumber={currentPDF.pageNumber}
        highlights={currentPDF.highlights}
      />
    </div>
  );
};

export default ChatTab;
